import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { isPlatformBrowser } from '@angular/common';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { extractYearFromString } from 'src/shared/utils';
import { ChartData } from 'src/shared/models';
import { TranslateService } from '@ngx-translate/core';
import { TranslateDataKeysService } from 'src/shared/services/translate-data-keys.service';

@Component({
  selector: 'atom-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss'],
})
export class LineChartComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root | undefined;
  private chart!: am5xy.XYChart;
  private legend!: am5.Legend;
  public exporting!:am5plugins_exporting.Exporting;

  @Input() chartData:ChartData[] = []; // Datos a graficar
  @Input() labelsXAxis: string = ''; // Etiquetas para el eje X
  @Input() dataFields: string[] = []; // Nombres de los campos en `chartData` que se usarán para las series de datos en el gráfico
  @Input() colors: string[] = []; // Colores para las líneas y elementos
  @Input() fileName: string = ''; // Nombre del archivo al exportar
  @Input() chartId: string = 'chart-' + Math.random().toString(36).substr(2, 9); // Genera un id único para renderizar el gráfico
  @Input() disabledSeries: string[] = []; // Series deshabilitadas por defecto
  @Input() showSeriesInTooltip: boolean = false; // Controla si incluir el nombre de la serie en el tooltip
  @Input() language:string = ""; // Idioma de la aplicación
  @Input() legendXPercent: number = 50; // Porcentaje horizontal (0 - 100) para posicionar la leyenda
  @Input() yAxisMinValue?: number; // Valor mínimo del eje Y
  @Input() labelSpacingXAxis?: number; // Valor de espaciado entre las etiquetas del eje X
  @Input() isLegendVisible?:boolean; // Debe estar visible la leyenda (etiquetas del gráfico)
  @Input() customTooltipName?: string = ''; // Nombre de la propiedad en los datos que se mostrará junto al valor del eje Y en el tooltip de los puntos del gráfico
  @Input() rotateXAxisLabels?:boolean = false; // Indica si las etiquetas del eje X deben rotarse en diagonal para mejorar su legibilidad
  @Input() legendWithTooltip?: string = ''; // Indica qué leyenda llevará el tooltip
  @Input() tooltipLegendName?: string = ''; // Nombre del tooltip para la leyenda especificada
  @Input() xAxisTitle?: string; // Título para el eje X
  @Input() yAxisTitle?: string; // Título para el eje Y

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object, // Inyecta el identificador de la plataforma (navegador o servidor)
    private zone: NgZone,  // Inyecta NgZone para manejar la detección de cambios y ejecutar código fuera del contexto de Angular
    private translateService: TranslateService, private translateDataKeysService: TranslateDataKeysService,
  ) {}

  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) { // Verifica si la aplicación se está ejecutando en el navegador
      this.zone.runOutsideAngular(() => {  // Ejecuta el código fuera del contexto de Angular para evitar la detección de cambios innecesaria
        f(); // Llama a la función proporcionada como parametro
      });
    }
  }

  ngOnInit(): void {}

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (this.root) {
      this.updateLegend(); // Actualiza la leyenda del gráfico cuando cambia el tamaño de la ventana
    }
  }

  // Método llamado después de que la vista del componente se haya inicializado
  ngAfterViewInit() {
    this.browserOnly(() => {   // Ejecuta el código solo en el navegador
      this.root = am5.Root.new(this.chartId);  // Crea una nueva instancia de 'am5.Root' asociada al elemento con un id único (contenedor donde se construirá el gráfico)
      this.renderChart();  // Llama al método para configurar y renderizar el gráfico
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.root && (changes['chartData'] || changes['language'])){
      this.root.dispose();
      this.root = undefined;
      // Vuelve a renderizar el gráfico
      this.browserOnly(() => {
        this.root = am5.Root.new(this.chartId);
        this.renderChart();
      });
    }
  }

  ngOnDestroy() {
    this.browserOnly(() => {   // Ejecuta el código solo en el navegador
      if (this.root) {   // Verifica si 'this.root' está definido
        this.root.dispose(); // Libera los recursos asociados a la instancia de 'am5.Root' para evitar fugas de memoria
      }
    });
  }

  private applyCustomChartTheme () {
    if(!this.root) return;

    // Crea un nuevo tema utilizando la instancia raíz del gráfico (`this.root`)
    // Este tema personalizado (`myTheme`) se puede usar para configurar estilos específicos en el gráfico
    const myTheme = am5.Theme.new(this.root);

    myTheme.rule('AxisLabel').setAll({ // Establece una regla para las etiquetas de los ejes (`AxisLabel`, tanto el eje X como Y)
      fontSize: '0.9em',  // Tamaño de la fuente de las etiquetas de los ejes
    });

    // Aplica los temas: animado, personalizado y responsive
    this.root.setThemes([
      am5themes_Animated.new(this.root),
      myTheme,
      am5themes_Responsive.new(this.root)
    ]);
  }

  private renderChart() {
    if (!this.root) return; // Si 'this.root' no esta definido, detiene la ejecución del método

    this.applyCustomChartTheme(); // Llama al método para establecer los temas personalizados al gráfico

    // Crea y agrega un gráfico XY al contenedor de la raíz del gráfico
    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        wheelX: 'panX', // Permite desplazar el gráfico horizontalmente con la rueda del mouse
        wheelY: 'zoomX',  // Permite hacer zoom horizontal con la rueda del mouse
        pinchZoomX: true, // Habilita el zoom con pellizco en el eje X para pantallas táctiles
        layout: this.root.verticalLayout // Establece que los elementos deben apilarse verticalmente (uno debajo del otro), el gráfico se colocará siguiendo esa disposición vertical
      })
    );

    // Crear eje Y para el gráfico
    let yAxis = this.chart.yAxes.push( // Agrega el eje Y al gráfico y guarda la referencia en la variable yAxis
      am5xy.ValueAxis.new(this.root, { // Crea una nueva instancia de ValueAxis para el eje Y, utilizando la raíz del gráfico (this.root)
        renderer: am5xy.AxisRendererY.new(this.root, {}),  // Configura el renderer del eje Y, define cómo se dibuja y muestra el eje
        // Configuración de la escala del eje Y
        // Establece el valor mínimo de la escala del eje Y. Si 'this.yAxisMinValue' está definido, se usa ese valor; de lo contrario, se usa 0
        min: this.yAxisMinValue ? this.yAxisMinValue : 0,
      })
    );

    // Crear eje X para el gráfico
    let xAxis = this.chart.xAxes.push(  // Agrega el eje X al gráfico y guarda la referencia en la variable xAxis
      am5xy.CategoryAxis.new(this.root, {  // Crea una nueva instancia de CategoryAxis para el eje X, usando la raíz del gráfico (this.root). CategoryAxis se usa para ejes que muestran categorías (en lugar de valores numéricos)
        renderer: am5xy.AxisRendererX.new(this.root, {  // Configura el renderer del eje X, define cómo se dibuja y muestra el eje
          minGridDistance: this.labelSpacingXAxis ? this.labelSpacingXAxis : 50, // Ajusta la distancia mínima entre etiquetas, si la propiedad 'this.labelSpacingXAxis' esta definida asigna su valor, si no por defecto será 50
         }),
        tooltip: am5.Tooltip.new(this.root, {}), // Configura el tooltip para el eje X (al pasar el mouse sobre el gráfico muestra los valores definidos en el eje X)
        categoryField: this.labelsXAxis, // Define el campo de datos que se usará para las categorías del eje X, que proviene de 'this.labelsXAxis'
      })
    );

    // Rotar las etiquetas en el eje X
    if(this.rotateXAxisLabels) {
      xAxis.get("renderer").labels.template.setAll({
       rotation: -45, // Ángulo en grados (-45 para diagonal)
       centerY: am5.p50, // Alinear verticalmente
       centerX: am5.p100, // Alinear horizontalmente
      });
    }

    // Configura el título para el eje X
    if(this.xAxisTitle) {
      const yearMatch = extractYearFromString(this.xAxisTitle);
      let nameBase = yearMatch ? this.xAxisTitle.replace(yearMatch, '').trim() : this.xAxisTitle; // Elimina el año, si no encuentra año, asigna el título original
      let xTitle = this.translateService.instant(`CHARTS.${nameBase}`);

      xAxis.children.unshift(
        am5.Label.new(this.root, {
          text: xTitle, // Tìtulo
          fontSize: 16,
          fontWeight: "bold",
          y: 10, // Distancia desde el eje
          x: am5.p50, // Centrado horizontalmente
          centerX: am5.p50, // Asegura el centrado horizontal
          centerY: am5.p50, // Asegura el centrado vertical
          fill: am5.color(0x74786E), // Color del texto
        })
      );
    }

    // Configura el título para el eje Y
    if(this.yAxisTitle){
      const yearMatch = extractYearFromString(this.yAxisTitle);
      let nameBase = yearMatch ? this.yAxisTitle.replace(yearMatch, '').trim() : this.yAxisTitle; // Elimina el año, si no encuentra año, asigna el título original
      let yTitle = this.translateService.instant(`CHARTS.${nameBase}`);

      yAxis.children.unshift(
        am5.Label.new(this.root, {
          text: yTitle, // Título
          rotation: -90, // Rotación para texto vertical
          fontSize: 16,
          fontWeight: "bold",
          x: 5, // Distancia desde el eje
          y: am5.p50, // Centrado verticalmente
          centerX: am5.p50, // Asegura el centrado horizontal
          fill: am5.color(0x74786E), // Color del texto
        })
      );
    }

    xAxis.data.setAll(this.chartData); // Establece los datos del eje X usando los datos proporcionados en 'this.chartData'

    // Itera sobre cada campo de datos en 'this.dataFields'
    this.dataFields.forEach((valueField, index) => {
      const yearMatch = extractYearFromString(valueField);
      let nameBase = yearMatch ? valueField.replace(yearMatch, '').trim() : valueField; // Elimina el año, si no encuentra año, asigna el nombre de la serie original
      let seriesName: string;

      if (!nameBase) {
        // Si el campo es solo un año
        seriesName = yearMatch || valueField;
      } else {
        // Traducir el nombre base
        const translated = this.translateService.instant(`CHARTS.${nameBase}`);
        seriesName = translated !== `CHARTS.${nameBase}` ? translated : nameBase;

        // Si hay año, se agrega al final
        if (yearMatch) {
          seriesName += ` ${yearMatch}`;
        }
      }

      // Agrega una nueva serie de líneas al gráfico
      let series = this.chart.series.push(
        am5xy.LineSeries.new(this.root!, {
          name: seriesName, // Establece el nombre de la serie basado en el campo de datos actual
          xAxis: xAxis,   // Asocia el eje X al gráfico para esta serie
          yAxis: yAxis,   // Asocia el eje Y al gráfico para esta serie
          valueYField: valueField,  // Define el campo de datos que se usará para los valores en el eje Y
          categoryXField: this.labelsXAxis, // Define el campo de datos que se usará para las categorías en el eje X
        })
      );

      // Verifica si el nombre de la serie actual (valueField) está en la lista `disabledSeries`
      if(this.disabledSeries.includes(valueField)){
        series.events.once('datavalidated', () => { // El evento 'datavalidated' se dispara cuando los datos de la serie han sido completamente procesados y la serie está lista para ser mostrada
          series.hide(); // Oculta la serie en el gráfico, pero sigue existiendo en el conjunto de datos del gráfico
        });
      }

      // Aplicar los colores a las series
      if (this.colors[index]) { // Verifica si existe un color para el índice actual en el array 'this.colors'
        series.set('stroke', am5.color(this.colors[index])); // Establece el color del borde (stroke) de la serie usando el color en 'this.colors[index]'
        series.set('fill', am5.color(this.colors[index]));   // Establece el color de relleno (fill) de la serie usando el mismo color en 'this.colors[index]'
      }

      series.data.setAll(this.chartData); // Establece todos los datos de la serie usando los datos proporcionados en 'this.chartData'
      series.appear(2000); // Hace que la serie aparezca en el gráfico con una animación que dura 2000 ms (2 segundos)

      // Personalizar el borde (stroke) de la serie
      series.strokes.template.setAll({
        strokeWidth: 2, // Ancho del borde de la línea
      });

      // Agrega un "bullet" (marca) a la serie para cada punto de datos
      series.bullets.push(() => {
          // Crea un nuevo círculo (bullet) para usar como marcador en el gráfico
          let bulletCircle = am5.Circle.new(this.root!, {
            radius: 5, // Establece el radio del círculo
            fill: series.get('fill'),  // Usa el color de relleno de la serie para el círculo
            // Define el texto que se mostrará en el tooltip cuando se pase el mouse sobre el círculo
            tooltipText: this.customTooltipName ? `{${this.customTooltipName}}: {valueY}` : this.showSeriesInTooltip ? `{name} in {categoryX}: {valueY}` : `{categoryX}: {valueY}`
          });

          // Crea un nuevo "bullet" y usa el círculo para el marcador
          return am5.Bullet.new(this.root!, {
            sprite: bulletCircle, // El círculo (sprite) que se muestra en cada punto de datos
          });
      });
    });

    this.chart.appear(2000, 100); // Hace que el gráfico aparezca con una animación que dura 2000 ms (2 segundos) y un retraso de 100 ms (100 milisegundos)

    // Agrega una leyenda al gráfico
    this.legend = this.chart.children.push(
      am5.Legend.new(this.root, {
        centerX: am5.p50, // Centra horizontalmente la leyenda en el gráfico
        x: am5.percent(this.legendXPercent),  // Posiciona la leyenda horizontalmente
        marginTop: 5, // Establece el espacio superior (margen) de la leyenda en 15 unidades. Esto añade un espacio entre la leyenda y el gráfico, separándolos visualmente
      })
    );

    // Verifica si la leyenda está visible y si se han configurado las propiedades necesarias para el tooltip
    if (this.isLegendVisible && this.legendWithTooltip && this.tooltipLegendName) {

      // Añade un adaptador al texto del tooltip de la leyenda
      this.legend.itemContainers.template.adapters.add("tooltipText", (tooltipText, target) => {
        // Obtiene el contexto de datos del elemento de la leyenda
        const dataContext = target.dataItem?.dataContext as any;
        const name = dataContext?._settings?.name;

        // Si el nombre de la leyenda coincide con el configurado, asigna el nuevo nombre al tooltip
        if (name === this.legendWithTooltip) {
          return this.tooltipLegendName;
        }
        // Si no, mantiene el texto original del tooltip
        return tooltipText;
      });

      // Asigna un tooltip global a la leyenda
      this.legend.itemContainers.template.set("tooltip", am5.Tooltip.new(this.root, {
        background: am5.RoundedRectangle.new(this.root, {
          fill: am5.color(0xFFFFFF), // Color de fondo del tooltip
        })
      }));
    }

    this.isLegendVisible && this.legend.data.setAll(this.chart.series.values);  // Evalua si 'this.isLegendVisible' es true para mostrar las etiquetas en el gráfico

    // Agrega un cursor al gráfico
    this.chart.set( 'cursor', am5xy.XYCursor.new(this.root, {})); // Permite configurar el comportamiento del cursor

    // Agrega las opciones para la exportación
    this.exporting = am5plugins_exporting.Exporting.new(this.root, {
      filePrefix: `${this.fileName}`, // Nombre de los archivos exportados
      menu: am5plugins_exporting.ExportingMenu.new(this.root, {}), // Añade un menú de opciones de exportación en el gráfico
      dataSource: this.translateDataKeysService.translateKeys(this.chartData, 'CHARTS'), // Especifica los datos que se exportarán, usando 'chartData' como fuente
      htmlOptions: {
        disabled: true, // Desactiva la exportación en formato HTML
      },
      jsonOptions: {
        disabled: true, // Desactiva la exportación en formato JSON
      }
    });
  }

  private updateLegend() {
    if (!this.root) return;

    // Actualiza la leyenda en función del tamaño de la ventana
    if(window.innerWidth <= 1024){
      this.legend.set('layout', this.root.verticalLayout);  // Cambiar la disposición a vertical para anchos de ventana menores a 992px
    } else {
      this.legend.set('layout', this.root.horizontalLayout);  // Cambiar la disposición a horizontal para anchos de ventana mayores a 992px
    }
  }

  private updateChartData() {
    // Actualizar los datos de la serie
    this.chart.series.each((series) => {
      series.data.setAll(this.chartData);
    });

    // Actualizar el eje X
    this.chart.xAxes.each((axis) => {
      axis.data.setAll(this.chartData);
    });

    // Actualizar el dataSource de la exportación
    if (this.exporting) {
      this.exporting.set("dataSource", this.chartData);
    }
  }
}
